import { Button, Col, Table as ReactTable, Row } from 'react-bootstrap';
import { Avatar, SalvButton, SalvModal } from '@salvapp/ui-components';
import React, { useEffect, useState } from 'react';
import { Form, Formik, FormikHelpers, FormikProps } from 'formik';
import * as Yup from 'yup';
import { ApiNote } from '../api/';
import FormField from '../form/FormField';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPenToSquare } from '@fortawesome/pro-solid-svg-icons/faPenToSquare';
import { FormikState } from 'formik/dist/types';
import FormattedDateTime from '../common/datetime/FormattedDateTime';
import Has from '../auth/Has';
import { CAN_ADD_NOTE } from '../auth/permissions';
import AlertDetailsPopover from '../alerts/AlertDetailsPopover';
import { InternalApiMonitoringAlert } from '../api-internal';
import { parseErrorMessage } from '../common/errors/helpers';

const validationSchema = Yup.object().shape({
  content: Yup.string().required('Required'),
});

export interface NotesModalFormValues {
  id?: number;
  content: string;
  alertId?: number;
}

interface NoteModalProps {
  onSubmit: (values: NotesModalFormValues) => Promise<void>;
  note?: ApiNote;
  dropdownItem?: boolean;
  alert?: InternalApiMonitoringAlert;
}

export const NotesModal = ({ note, onSubmit, dropdownItem, alert }: NoteModalProps) => {
  const [showModal, setShowModal] = useState(false);
  const textAreaRef = React.createRef<HTMLTextAreaElement>();
  useEffect(() => {
    if (showModal) {
      textAreaRef.current?.focus();
    }
  }, [showModal]);

  const initialValues =
    note !== undefined
      ? {
          ...note,
        }
      : {
          content: '',
          alertId: alert && alert.id,
        };

  const initialFormikState: Partial<FormikState<NotesModalFormValues>> = {
    values: initialValues,
    isSubmitting: false,
    isValidating: false,
  };

  const attributes = ['Date', 'Details', 'Assignee', 'Status'];

  const submit = async (
    values: NotesModalFormValues,
    { setStatus, setSubmitting, resetForm }: FormikHelpers<NotesModalFormValues>
  ) => {
    try {
      await onSubmit(values);
      setShowModal(false);
      resetForm(initialFormikState);
    } catch (e) {
      setStatus(await parseErrorMessage(e));
    }
    setSubmitting(false);
  };

  return (
    <Formik initialValues={initialValues} onSubmit={submit} validationSchema={validationSchema}>
      {({ isSubmitting }: FormikProps<NotesModalFormValues>) => (
        <>
          <Has allPermissions={CAN_ADD_NOTE}>
            <>
              {dropdownItem ? (
                <Button className='dropdown-item' onClick={() => setShowModal(true)}>
                  Add note
                </Button>
              ) : (
                (note && (
                  <Button onClick={() => setShowModal(true)} variant='outline-danger' className='right-button'>
                    Edit
                  </Button>
                )) || (
                  <Button variant='secondary' size='sm' onClick={() => setShowModal(true)}>
                    <FontAwesomeIcon icon={faPenToSquare} className='fe me-2' />
                    Add note
                  </Button>
                )
              )}
            </>
          </Has>
          <SalvModal show={showModal} onHide={() => setShowModal(false)}>
            <Form>
              <SalvModal.Header closeButton>Add note {alert && 'to alert'}</SalvModal.Header>
              <SalvModal.Body>
                {alert && (
                  <ReactTable striped hover size='sm' className='card-table'>
                    <thead>
                      <tr>
                        {attributes.map((a) => (
                          <th key={a}>
                            <div className='attribute-header'>{a}</div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>
                          <FormattedDateTime date={alert.createdTime} />
                        </td>
                        <td>
                          <AlertDetailsPopover alert={alert} />
                        </td>
                        <td>
                          {alert.assignee ? (
                            <>
                              <Avatar email={alert.assignee?.email} />
                              <span className='ms-2'>{alert.assignee?.name}</span>
                            </>
                          ) : (
                            '--'
                          )}
                        </td>
                        <td>{alert.status[0].toUpperCase() + alert.status.substring(1).toLowerCase()}</td>
                      </tr>
                    </tbody>
                  </ReactTable>
                )}

                <Row>
                  <Col md={12}>
                    <FormField inputref={textAreaRef} type='textarea' name='content' rows={5} label='Comment' />
                  </Col>
                </Row>
              </SalvModal.Body>

              <SalvModal.Footer>
                <SalvButton label='Close' variant='muted' onClick={() => setShowModal(false)} />
                <SalvButton disabled={isSubmitting} type='submit' label='Save' variant='primary' />
              </SalvModal.Footer>
            </Form>
          </SalvModal>
        </>
      )}
    </Formik>
  );
};

export default NotesModal;
